import { ChangeDetectionStrategy, Component, Signal, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HD_BODYGRAPH_GATE_TO_CENTER } from '../hd-bodygraph-metadata';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { loadHdChartGlobalSettings } from '../chart-tabs/hd-chart-global-settings';
import { HdGateGraphComponent } from './hd-gate-graph.component';
import { HdGateGraphContext } from './hd-gate-graph.models';
import { HdGateGraphPaletteId } from './hd-gate-graph-palette';
import { HD_GATE_GRAPH_LAYOUT } from './hd-gate-graph-layout';

/**
 * Paneel-wrapper van de radiale poort-visualisatie: haalt de vertaalde keynotes
 * op en geeft ze door aan {@link HdGateGraphComponent}, die puur presentational is.
 *
 * De context komt op twee manieren binnen — hetzelfde dubbele patroon als
 * {@link HdGateInfoRequestedComponent}:
 *  1. als `[context]`-input bij direct gebruik in een template;
 *  2. via het `FLOATING_PANEL_DATA`-token wanneer het paneel dynamisch gemount
 *     wordt door de {@link FloatingPanelService}.
 */
@Component({
  selector: 'hd-gate-graph-panel',
  templateUrl: './hd-gate-graph-panel.component.html',
  styleUrl: './hd-gate-graph-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateGraphComponent],
})
export class HdGateGraphPanelComponent {
  readonly context = input<HdGateGraphContext | undefined>(undefined);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdGateGraphContext>
    | null;
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);

  protected readonly toggleLabels = computed(() => {
    const lang = this.contentLang.effectiveLang();
    return {
      showFull: this.transloco.translate('humanDesign.gateGraph.showFull', undefined, lang),
      showHighlight: this.transloco.translate('humanDesign.gateGraph.showHighlight', undefined, lang),
    };
  });

  protected readonly effectiveContext = computed<HdGateGraphContext | undefined>(
    () => this.context() ?? this.panelData?.() ?? undefined,
  );

  protected readonly gate = computed(() => this.effectiveContext()?.gate ?? 0);

  private readonly gateInfo = computed(() =>
    this.gateInfoService.gateInfoIn(this.gate(), this.contentLang.effectiveLang()));

  protected readonly keynote1 = computed(() => this.gateInfo().translations.keynote1);
  protected readonly keynote2 = computed(() => this.gateInfo().translations.keynote2);

  protected readonly lineKeynotes = computed(() =>
    this.gateInfo().translations.lines.map(line => line.keynote));

  /**
   * Center-shape asset van de poort. De fill zit in het bestand gebakken, dus
   * het icoon draagt meteen de eigen chart-kleur van het center.
   */
  protected readonly centerIconUrl = computed(() => {
    const center = HD_BODYGRAPH_GATE_TO_CENTER[this.gate()];
    return center === undefined ? undefined : `assets/human-design/center-shapes/${center}.svg`;
  });

  /** Vertaalde center-naam — tooltip op het icoon in de poortcirkel. */
  protected readonly centerName = computed(() =>
    this.gateInfoService.centerLabelIn(this.gateInfo().center, this.contentLang.effectiveLang()));

  /**
   * Ramp en spreiding komen uit de globale chart-settings. Ze worden bij het
   * openen gelezen; een instellingswissel raakt panelen die daarna opengaan.
   */
  private readonly settings = loadHdChartGlobalSettings();
  protected readonly palette: HdGateGraphPaletteId = this.settings.gateGraphPalette ?? 'aarde';
  protected readonly spacing: number =
    this.settings.gateGraphSpacing ?? HD_GATE_GRAPH_LAYOUT.spacing.default;
}
